import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";

import { Trans, useTranslation } from "react-i18next";

import Particle from "../Particle";

const VOLUNTEER_FORM_URL =
  "https://docs.google.com/forms/d/1Q2Lc15IwomBIFeSAxIfsK56xI3vkCjvKW8yXQcWQvmM/edit";

function VolunteerOpportunities() {
  const { t } = useTranslation();

  const ways = t("volunteerOpportunitiesPage.ways.items", { returnObjects: true });
  const wayItems = Array.isArray(ways) ? ways : [];

  const who = t("volunteerOpportunitiesPage.who.items", { returnObjects: true });
  const whoItems = Array.isArray(who) ? who : [];

  const approach = t("volunteerOpportunitiesPage.approach.values", { returnObjects: true });
  const approachItems = Array.isArray(approach) ? approach : [];

  const gains = t("volunteerOpportunitiesPage.gains.items", { returnObjects: true });
  const gainItems = Array.isArray(gains) ? gains : [];

  return (
    <Container fluid className="about-section volunteer-opportunities-page">
      <Particle />

      <Container className="volunteer-opportunities-container">
        <header className="volunteer-opportunities-hero">
          <div className="volunteer-opportunities-eyebrow">{t("volunteerOpportunitiesPage.eyebrow")}</div>

          <h1 className="volunteer-opportunities-hero-title">
            <Trans
              i18nKey="volunteerOpportunitiesPage.hero.title"
              components={{ purple: <span className="purple" /> }}
            />
          </h1>

          <p className="volunteer-opportunities-hero-lead">{t("volunteerOpportunitiesPage.hero.lead")}</p>

          <div
            className="volunteer-opportunities-hero-cta"
            aria-label={t("volunteerOpportunitiesPage.a11y.ctaLabel")}
          >
            <Button
              as="a"
              href={VOLUNTEER_FORM_URL}
              target="_blank"
              rel="noreferrer"
              className="custom-yellow-btn volunteer-opportunities-btn"
            >
              {t("volunteerOpportunitiesPage.cta.apply")}
            </Button>
          </div>
        </header>

        <section className="volunteer-opportunities-section" aria-label={t("volunteerOpportunitiesPage.a11y.intro")}
        >
          <Row className="volunteer-opportunities-intro" xs={1} md={2}>
            <Col className="about-top-col">
              <div className="policy-card volunteer-opportunities-panel">
                <h2 className="volunteer-opportunities-heading volunteer-opportunities-heading--left">
                  <Trans
                    i18nKey="volunteerOpportunitiesPage.headings.getInvolved"
                    components={{ purple: <span className="purple" /> }}
                  />
                </h2>
                <p className="about-text volunteer-opportunities-lead">{t("volunteerOpportunitiesPage.intro.p1")}</p>
                <p className="about-text">{t("volunteerOpportunitiesPage.intro.p2")}</p>
                <p className="about-text">{t("volunteerOpportunitiesPage.intro.p3")}</p>
              </div>
            </Col>

            <Col className="about-top-col">
              <div className="policy-card volunteer-opportunities-panel volunteer-opportunities-panel--accent">
                <h2 className="volunteer-opportunities-heading volunteer-opportunities-heading--left">
                  <Trans
                    i18nKey="volunteerOpportunitiesPage.headings.coreMessage"
                    components={{ purple: <span className="purple" /> }}
                  />
                </h2>

                <div className="volunteer-opportunities-quote">
                  <div className="volunteer-opportunities-quote-label">{t("volunteerOpportunitiesPage.core.label")}</div>
                  <div className="volunteer-opportunities-quote-text">{t("volunteerOpportunitiesPage.core.text")}</div>
                </div>

                <p className="volunteer-opportunities-muted">{t("volunteerOpportunitiesPage.core.note")}</p>
              </div>
            </Col>
          </Row>
        </section>

        <section className="volunteer-opportunities-section" aria-label={t("volunteerOpportunitiesPage.a11y.ways")}
        >
          <h2 className="volunteer-opportunities-heading">
            <Trans
              i18nKey="volunteerOpportunitiesPage.headings.ways"
              components={{ purple: <span className="purple" /> }}
            />
          </h2>
          <p className="volunteer-opportunities-sublead">{t("volunteerOpportunitiesPage.ways.lead")}</p>

          <div className="volunteer-opportunities-grid">
            {wayItems.map((item) => (
              <div key={item.title} className="policy-card volunteer-opportunities-card">
                <div className="volunteer-opportunities-card-kicker">{item.kicker}</div>
                <h3 className="volunteer-opportunities-card-title">{item.title}</h3>
                <p className="volunteer-opportunities-card-lead">{item.lead}</p>
                <ul className="volunteer-opportunities-list">
                  {(Array.isArray(item.bullets) ? item.bullets : []).map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="volunteer-opportunities-section" aria-label={t("volunteerOpportunitiesPage.a11y.who")}
        >
          <Row className="volunteer-opportunities-split" xs={1} md={2}>
            <Col className="about-top-col">
              <div className="policy-card volunteer-opportunities-panel">
                <h2 className="volunteer-opportunities-heading volunteer-opportunities-heading--left">
                  <Trans
                    i18nKey="volunteerOpportunitiesPage.headings.who"
                    components={{ purple: <span className="purple" /> }}
                  />
                </h2>

                <p className="volunteer-opportunities-sublead volunteer-opportunities-sublead--left">
                  {t("volunteerOpportunitiesPage.who.lead")}
                </p>

                <div className="volunteer-opportunities-chipset" aria-label={t("volunteerOpportunitiesPage.a11y.whoList")}
                >
                  {whoItems.map((label) => (
                    <span key={label} className="volunteer-opportunities-chip">
                      {label}
                    </span>
                  ))}
                </div>

                <p className="volunteer-opportunities-muted">{t("volunteerOpportunitiesPage.who.note")}</p>
              </div>
            </Col>

            <Col className="about-top-col">
              <div className="policy-card volunteer-opportunities-panel">
                <h2 className="volunteer-opportunities-heading volunteer-opportunities-heading--left">
                  <Trans
                    i18nKey="volunteerOpportunitiesPage.headings.approach"
                    components={{ purple: <span className="purple" /> }}
                  />
                </h2>

                <ul className="volunteer-opportunities-values">
                  {approachItems.map((value) => (
                    <li key={value.label} className="volunteer-opportunities-value">
                      <div className="volunteer-opportunities-value-label">{value.label}</div>
                      <div className="volunteer-opportunities-value-text">{value.text}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        </section>

        <section className="volunteer-opportunities-section" aria-label={t("volunteerOpportunitiesPage.a11y.gains")}
        >
          <Row className="volunteer-opportunities-gains" xs={1} md={2}>
            <Col className="about-top-col">
              <div className="policy-card volunteer-opportunities-panel volunteer-opportunities-panel--accent">
                <h2 className="volunteer-opportunities-heading volunteer-opportunities-heading--left">
                  <Trans
                    i18nKey="volunteerOpportunitiesPage.headings.gains"
                    components={{ purple: <span className="purple" /> }}
                  />
                </h2>
                <p className="volunteer-opportunities-sublead volunteer-opportunities-sublead--left">
                  {t("volunteerOpportunitiesPage.gains.lead")}
                </p>
                <ul className="volunteer-opportunities-list volunteer-opportunities-list--tight">
                  {gainItems.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </div>
            </Col>

            <Col className="about-top-col">
              <div className="policy-card volunteer-opportunities-cta-card">
                <h2 className="volunteer-opportunities-heading volunteer-opportunities-heading--left">
                  <Trans
                    i18nKey="volunteerOpportunitiesPage.headings.start"
                    components={{ purple: <span className="purple" /> }}
                  />
                </h2>

                <p className="volunteer-opportunities-sublead volunteer-opportunities-sublead--left">
                  {t("volunteerOpportunitiesPage.start.lead")}
                </p>

                <Button
                  as="a"
                  href={VOLUNTEER_FORM_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="custom-yellow-btn volunteer-opportunities-btn"
                >
                  {t("volunteerOpportunitiesPage.cta.apply")}
                </Button>

                <div className="volunteer-opportunities-footnote">{t("volunteerOpportunitiesPage.start.footnote")}</div>
              </div>
            </Col>
          </Row>
        </section>
      </Container>
    </Container>
  );
}

export default VolunteerOpportunities;
